import React from "react";
import { TrendingUp, TrendingDown } from "lucide-react";

/**
 * StatCard - Premium dashboard KPI card
 * TradebotX-inspired: gradient icon badge, large value, trend indicator, gradient top accent on hover
 */
const StatCard = React.memo(function StatCard({
  title,
  value,
  change,
  changeLabel,
  icon: Icon,
  iconColor = "var(--accent-primary)",
  prefix = "",
  suffix = "",
  className = "",
  progressBar = null,
  subtitle = null,
  sparkline = null,
  valueColor = null,
}) {
  const isPositive = change > 0;
  const isNeutral = change === 0 || change === undefined || change === null;

  return (
    <div className={`card-stat ${className}`}>
      <div className="flex items-start justify-between mb-4">
        <div
          className="flex items-center justify-center rounded-xl"
          style={{
            width: 46,
            height: 46,
            background: `linear-gradient(135deg, ${iconColor}20, ${iconColor}10)`,
            border: `1px solid ${iconColor}25`,
          }}
        >
          {Icon && <Icon size={22} style={{ color: iconColor }} />}
        </div>
        {!isNeutral && (
          <div
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg"
            style={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: isPositive ? "var(--color-success)" : "var(--color-error)",
              background: isPositive ? "var(--color-success-bg)" : "var(--color-error-bg)",
            }}
          >
            {isPositive ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
            {Math.abs(change)}%
          </div>
        )}
      </div>

      <p
        className="font-display text-2xl font-extrabold tracking-tight mb-1"
        style={{
          color: valueColor || "var(--text-primary)",
          letterSpacing: "-0.02em",
          lineHeight: 1.2,
        }}
      >
        {prefix}
        {value}
        {suffix && (
          <span
            style={{
              fontSize: "0.85rem",
              fontWeight: 500,
              marginLeft: 4,
              color: "var(--text-muted)",
            }}
          >
            {suffix}
          </span>
        )}
      </p>

      <p
        className="text-[11px] font-semibold uppercase tracking-wider"
        style={{ color: "var(--text-muted)" }}
      >
        {title}
      </p>

      {subtitle && (
        <p className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>
          {subtitle}
        </p>
      )}

      {sparkline && <div style={{ marginTop: 12, height: 32 }}>{sparkline}</div>}

      {progressBar && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>Utilization</span>
            <span
              style={{
                fontSize: "0.7rem",
                fontWeight: 600,
                color: progressBar.color || "var(--accent-primary)",
              }}
            >
              {Math.min(100, Math.round((progressBar.value / progressBar.max) * 100))}%
            </span>
          </div>
          <div
            style={{
              height: 5,
              background: "var(--bg-input)",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <div
              className="animate-progress-fill"
              style={{
                height: "100%",
                width: `${Math.min(100, (progressBar.value / progressBar.max) * 100)}%`,
                background: `linear-gradient(90deg, ${progressBar.color || "var(--accent-primary)"}, ${progressBar.color || "var(--accent-secondary)"})`,
                borderRadius: 3,
                transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />
          </div>
        </div>
      )}

      {changeLabel && (
        <p className="text-[11px] mt-2" style={{ color: "var(--text-muted)" }}>
          {changeLabel}
        </p>
      )}
    </div>
  );
});

export default StatCard;
